import { useCallback, useEffect, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';
import { PendingReward, PendingRewardsResponse } from './types';

/**
 * Custom-reward moderation queue (SSA-815 endpoints): list pending, approve, or
 * reject (reject requires a reason recorded on the audit row). Re-fetches after
 * each decision.
 */
export function useRewardModeration(env: Env) {
  const { request } = useAdminApi();
  const [items, setItems] = useState<PendingReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<PendingRewardsResponse>({
        env,
        path: '/admin/gamification/rewards/pending',
      });
      setItems(data.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load pending rewards');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request, env]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const approve = useCallback(
    async (rewardId: string): Promise<boolean> => {
      setActionError(null);
      try {
        await request({
          env,
          path: `/admin/gamification/rewards/${rewardId}/approve`,
          method: 'POST',
        });
        await refetch();
        return true;
      } catch (e) {
        setActionError(e instanceof ApiError ? e.message : 'Approve failed');
        return false;
      }
    },
    [request, env, refetch],
  );

  const reject = useCallback(
    async (rewardId: string, reason: string): Promise<boolean> => {
      setActionError(null);
      try {
        await request({
          env,
          path: `/admin/gamification/rewards/${rewardId}/reject`,
          method: 'POST',
          body: { reason },
        });
        await refetch();
        return true;
      } catch (e) {
        setActionError(e instanceof ApiError ? e.message : 'Reject failed');
        return false;
      }
    },
    [request, env, refetch],
  );

  return { items, loading, error, actionError, refetch, approve, reject };
}
